import React from "react";
import {
  Cloud,
  ShieldCheck,
  Code2,
  Headphones,
  Network,
  Settings,
  Star,
  Users,
  Briefcase,
  CheckCircle2,
} from "lucide-react";

const services = [
  {
    title: "Managed IT Services",
    description:
      "End-to-end management of your systems — from hardware and software to performance monitoring.",
    icon: Network,
  },
  {
    title: "Cloud Solutions",
    description:
      "Cloud hosting, migration, and backups tailored for flexibility and data safety.",
    icon: Cloud,
  },
  {
    title: "Cybersecurity",
    description:
      "Protection from evolving threats using encryption, firewalls, audits, and threat detection.",
    icon: ShieldCheck,
  },
  {
    title: "Custom Software Development",
    description:
      "Powerful web, desktop, and mobile solutions built specifically for your business workflows.",
    icon: Code2,
  },
  {
    title: "IT Consulting",
    description:
      "Expert strategy and guidance for your digital roadmap, from upgrades to innovation.",
    icon: Settings,
  },
  {
    title: "Help Desk Support",
    description:
      "Responsive technical support from trained professionals to resolve IT issues fast.",
    icon: Headphones,
  },
];

const Itservices = () => {
  return (
    <div className="text-gray-800 flex items-center justify-center flex-col">
      <section className="text-center w-full p-6 bg-[#c4122f] text-white mb-16">
        <h1 className="text-4xl font-extrabold mb-4">
          Collaboration with Newway IT
        </h1>
        <p className="text-lg max-w-4xl mx-auto">
          We’ve proudly partnered with <strong>Newway IT</strong> to deliver
          cutting-edge IT services tailored to modern business needs. This
          partnership enhances our service offerings and helps our clients stay
          ahead in technology.
        </p>
      </section>

      <section className="mb-16 max-w-7xl mx-auto text-center">
        <h2 className="text-2xl font-semibold mb-3 text-[#c4122f]">
          About Newway IT
        </h2>
        <p className="text-gray-700 leading-relaxed">
          Newway IT is a forward-thinking technology company that blends
          innovation with reliability. Their commitment to client success,
          paired with a team of industry-certified experts, ensures that your IT
          infrastructure is not just supported — it's future-proof.
        </p>
      </section>

      <section className="mb-20 max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
        {[
          {
            title: "Proven Expertise",
            desc: "Backed by years of experience and a results-driven team.",
            icon: Star,
          },
          {
            title: "Client-Centric",
            desc: "Their custom strategies revolve around real business goals.",
            icon: Users,
          },
          {
            title: "Tech Leadership",
            desc: "Innovation-led solutions that push boundaries.",
            icon: Briefcase,
          },
        ].map(({ title, desc, icon: Icon }, i) => (
          <div
            key={i}
            className="p-6 bg-white border rounded-xl shadow-sm hover:shadow-md transition text-center"
          >
            <Icon className="w-8 h-8 mx-auto mb-3 text-[#c4122f]" />
            <h3 className="font-semibold text-lg mb-1">{title}</h3>
            <p className="text-sm text-gray-600">{desc}</p>
          </div>
        ))}
      </section>

      <section className="mb-20">
        <h2 className="text-2xl font-bold text-center mb-8 text-[#c4122f]">
          Services by Newway IT
        </h2>
        <div className="grid max-w-7xl gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {services.map(({ icon: Icon, title, description }, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-xl border shadow-sm hover:shadow-lg transition group"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#c4122f] bg-opacity-10 mb-4">
                <Icon className="text-[#c4122f] w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{title}</h3>
              <p className="text-sm text-gray-600">{description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-20 max-w-7xl mx-auto">
        <h2 className="text-2xl font-semibold text-center mb-6 text-[#c4122f]">
          Client Benefits
        </h2>
        <ul className="grid sm:grid-cols-2 gap-4 text-sm text-gray-700">
          {[
            "Certified and experienced IT engineers",
            "Cloud-native infrastructure support",
            "Stronger cybersecurity defenses",
            "Seamless user and system support",
            "Increased uptime and faster recovery",
            "Tailored roadmaps and automation",
          ].map((item, i) => (
            <li key={i} className="flex items-center gap-2">
              <CheckCircle2 className="text-[#c4122f]" size={18} />
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-20 bg-white border rounded-xl p-8 max-w-5xl mx-auto text-center shadow-sm">
        <p className="italic text-lg text-gray-700">
          “Since partnering with Newway IT, our infrastructure is more stable
          and scalable than ever. Their dedication and technical excellence
          speak for themselves.”
        </p>
        <p className="text-sm text-gray-500 mt-4">
          — CEO, ARGUS Security International
        </p>
      </section>

      <section className="text-center">
        <p className="text-lg mb-4 font-medium text-gray-800">
          Explore how Newway IT can transform your business.
        </p>
        <a
          href="https://newwayit.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-[#c4122f] text-white px-8 py-3 rounded-full hover:bg-red-700 transition font-semibold"
        >
          Visit Newway IT Website
        </a>
      </section>
    </div>
  );
};

export default Itservices;
